/**
 * API Shape Normalization Utilities
 * ==================================
 * Defensive utilities for handling inconsistent API response shapes.
 * 
 * Why this exists:
 * - Backend APIs sometimes return arrays, sometimes { items: [] }, { data: [] }, { likers: [] }
 * - Frontend code that does `response.map(...)` crashes when shape changes
 * - This normalizer acts as an "airbag" at the seam between API and UI
 * 
 * Usage:
 *   import { normalizeList } from '../utils/normalize';
 *   const items = normalizeList(response.data);
 *   // Now safe to call items.map(...), items.length, etc.
 */

const isDev = process.env.NODE_ENV !== 'production';

/**
 * Normalize any API response into an array.
 * 
 * Handles:
 * - Direct arrays: [...]
 * - Null/undefined: returns []
 * - Wrapped objects: { items: [...] }, { data: [...] }, { likers: [...] }
 * - Custom keys via preferredKeys param
 * - Objects keyed by ID: { "abc123": {...}, "def456": {...} } → [...]
 * 
 * @param {any} input - The API response to normalize
 * @param {string[]} preferredKeys - Additional keys to check for array data (e.g., ['gifts', 'notifications'])
 * @returns {Array} - Always returns an array (empty if input is unusable)
 */
export function normalizeList(input, preferredKeys = []) {
  // Already an array - pass through
  if (Array.isArray(input)) return input;
  
  // Null/undefined/falsy - empty array
  if (!input) return [];
  
  // Check preferred keys first (caller knows what to expect)
  for (const key of preferredKeys) {
    if (Array.isArray(input[key])) return input[key];
  }
  
  // Common API wrapper shapes
  const commonKeys = ['items', 'data', 'results', 'list', 'likers', 'matches', 'gifts', 'notifications', 'events'];
  for (const key of commonKeys) {
    if (Array.isArray(input[key])) {
      // Dev-only warning: shape wasn't what we expected
      if (isDev && preferredKeys.length > 0 && !preferredKeys.includes(key)) {
        console.warn(
          `[normalizeList] Found array in unexpected key "${key}". ` +
          `Expected one of: [${preferredKeys.join(', ')}]. ` +
          `Consider updating the API or the preferredKeys param.`
        );
      }
      return input[key];
    }
  }
  
  // Object keyed by ID (e.g., { "id1": {...}, "id2": {...} })
  if (typeof input === 'object' && !Array.isArray(input)) {
    const values = Object.values(input);
    // Only treat as keyed object if values look like items (objects with id/data)
    if (values.length > 0 && typeof values[0] === 'object') {
      if (isDev) {
        console.warn(
          `[normalizeList] Converted object-keyed response to array. ` +
          `Keys: [${Object.keys(input).slice(0, 3).join(', ')}${Object.keys(input).length > 3 ? '...' : ''}]`
        );
      }
      return values;
    }
  }
  
  // Completely unexpected shape - log in dev
  if (isDev) {
    console.warn(
      `[normalizeList] Unexpected response shape. ` +
      `Type: ${typeof input}, ` +
      `Keys: ${typeof input === 'object' ? Object.keys(input).join(', ') : 'N/A'}`,
      input
    );
  }
  
  return [];
}

/**
 * Normalize a count value from API response.
 * 
 * Handles:
 * - Direct numbers
 * - Wrapped: { count: n }, { total: n }, { length: n }
 * - Arrays (returns length)
 * - Null/undefined (returns 0)
 * 
 * @param {any} input - The API response
 * @param {number} fallback - Default value if count can't be determined
 * @returns {number}
 */
export function normalizeCount(input, fallback = 0) {
  if (typeof input === 'number') return input;
  if (!input) return fallback;
  if (Array.isArray(input)) return input.length;
  if (typeof input.count === 'number') return input.count;
  if (typeof input.total === 'number') return input.total;
  if (typeof input.length === 'number') return input.length;
  
  // Try to normalize as list and get length
  const list = normalizeList(input);
  return list.length || fallback;
}

/**
 * Safe property access with fallback.
 * Useful when API sometimes omits fields.
 * 
 * @param {object} obj - The object to access
 * @param {string} path - Dot-notation path (e.g., 'user.profile.name')
 * @param {any} fallback - Default value if path doesn't exist
 * @returns {any}
 */
export function safeGet(obj, path, fallback = undefined) {
  if (!obj || !path) return fallback;
  
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current == null || typeof current !== 'object') {
      return fallback;
    }
    current = current[key];
  }
  
  return current ?? fallback;
}
